// Copyright (c) 2021, Comsearch, Inc., All rights reserved

/**
 * @typedef {Object} PassiveRepeater
 * @property {Array} antennas - array of antenna data for the passive repeater
 * @property {Array} backToBack - array of back-to-back information for the passive repeater.
 */

/**
 * @typedef {Object} PassiveRepeaterAntenna
 * @property {int} segmentEndAntennaId
 * @property {int} secondSegmentEndAntennaId
 */

/**
 * Flips the antennas for a given passive repeater.<br />
 * back-to-back antennas are reversed in their order<br />
 * billboard antennas have their segmentEndAntennaId <> secondSegmentEndAntennaId swapped
 * @param {PassiveRepeater}
 * @returns {Array<PassiveRepeaterAntenna>}
 */
const flipPassiveRepeaterAntennas = ({ antennas, backToBack }) => {
  if (antennas.length > 2) {
    throw new Error('Error: current implementation of Flip Path does not support Passive Repeaters with more than two antennas.')

  // this should never be possible but just in case, let's check
  // make sure that either we have two antennas when 'back to back'
  // or we have only one antenna when billboard.
  } else if (backToBack.length === 0 && antennas.length !== 1) {
    throw new Error(`The Billboard Passive Repeater data is malformed and cannot be correctly flipped. ${antennas.length} antennas were assigned when we expected only 1`)
  } else if (backToBack.length === 1 && antennas.length !== 2) {
    const l = antennas.length
    throw new Error(`The Back-to-Back Passive Repeater data is malformed and cannot be correctly flipped. ${l} ${l === 1 ? 'antenna was' : 'antennas were'} assigned when we expected exactly 2.`)
  }

  // if we're using back-to-back antennas for this passive repeater, reverse the antennas
  if (backToBack.length === 1) {
    return [...antennas].reverse()

    // if we're not back-to-back, we're billboard and as a result, we only have one antenna which represents two
    // passive segment ends. To work around this, the antenna will store two segment end ids. Swap these two.
  } else {
    const antenna = antennas[0]
    const segmentEndAntennaId = antenna.secondSegmentEndAntennaId
    const secondSegmentEndAntennaId = antenna.segmentEndAntennaId

    return [{
      ...antenna,
      segmentEndAntennaId,
      secondSegmentEndAntennaId,
    }]
  }
}

export default flipPassiveRepeaterAntennas
